import React, { ChangeEvent } from "react";

import { DateTime, Info } from "luxon";

import Calendar2, { ICalendar2ButtonCallbacks } from "../Calendar2";
import DelayInput from "../DelayInput";
import ValidateInput from "../ValidateInput";

import { V2, apiGet } from "../../Api";
import { formatTimeLocal } from "../../Utilities";
import withTitle from "../withTitle";
import withAuth from "../withAuth";
import Checkbox from "../Checkbox";
import { IAuthProps } from "../AuthContext";

interface IScheduleOfficeViewState {
    events: any;
    isLoading: boolean | string;

    canEditCalendar: boolean;
    month: string;

    displayAfter: DateTime;
    displayBefore: DateTime;

    searchLocation: string;

    calLocation: string;
    calItem: any;

    calSundayEnabled: boolean;
    calSundayOpen: string;
    calSundayClose: string;

    calMondayEnabled: boolean;
    calMondayOpen: string;
    calMondayClose: string;

    calTuesdayEnabled: boolean;
    calTuesdayOpen: string;
    calTuesdayClose: string;

    calWednesdayEnabled: boolean;
    calWednesdayOpen: string;
    calWednesdayClose: string;

    calThursdayEnabled: boolean;
    calThursdayOpen: string;
    calThursdayClose: string;

    calFridayEnabled: boolean;
    calFridayOpen: string;
    calFridayClose: string;

    calSaturdayEnabled: boolean;
    calSaturdayOpen: string;
    calSaturdayClose: string;

    employees: any[];
    locations: any[];

    [name: string]: any;
}

class ScheduleOfficeView extends React.Component<IAuthProps, IScheduleOfficeViewState> {
    constructor(props: Readonly<IAuthProps>) {
        super(props);

        const today = DateTime.local();

        const state: IScheduleOfficeViewState = {
            events: {},
            isLoading: false,

            canEditCalendar: props.authContext.hasRight("sched:off:write"),
            month: today.startOf("month").toFormat("yyyy-MM"),

            displayBefore: today.endOf("month"),
            displayAfter: today.startOf("month"),

            searchLocation: "",

            calEmployee: "",
            calLength: "",
            calLengthType: "1",
            calLocation: "",
            calRepeat: false,
            calRepeatType: "week",
            calRepeatLength: "1",
            calItem: {},
            calHideRepeat: false,

            employees: [],
            locations: [],

            calSundayEnabled: false,
            calSundayOpen: "",
            calSundayClose: "",

            calMondayEnabled: false,
            calMondayOpen: "",
            calMondayClose: "",

            calTuesdayEnabled: false,
            calTuesdayOpen: "",
            calTuesdayClose: "",

            calWednesdayEnabled: false,
            calWednesdayOpen: "",
            calWednesdayClose: "",

            calThursdayEnabled: false,
            calThursdayOpen: "",
            calThursdayClose: "",

            calFridayEnabled: false,
            calFridayOpen: "",
            calFridayClose: "",

            calSaturdayEnabled: false,
            calSaturdayOpen: "",
            calSaturdayClose: "",
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.displayRangeChanged = this.displayRangeChanged.bind(this);
        this.scheduleRender = this.scheduleRender.bind(this);
        this.handleDialogChange = this.handleDialogChange.bind(this);
        this.handleItemEdit = this.handleItemEdit.bind(this);
        this.beforeShowAddInterface = this.beforeShowAddInterface.bind(this);
        this.handleBeforeItemEdit = this.handleBeforeItemEdit.bind(this);
    }

    public async componentDidMount() {
        try {
            const locations = await apiGet<any[]>("locations?type=office");

            this.setState({
                locations
            });
        }
        catch (e) {
            //
        }
    }

    public render() {
        return (
            <div className="viewer-root viewer-full full-height">
                <div className="filter-control-box">
                    <DelayInput type="month" className="fill-space" timeout={500} value={this.state.month} name="month" onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                </div>
                <div className="spinner-overlay-root full-height">
                    <Calendar2 className="full-height" displayMonth={this.state.month} highlightDate={new Date()} showDetailsOnClick={true} showButtonsOnDetails={true} allowAdd={this.state.canEditCalendar} allowEdit={this.state.canEditCalendar} disableDelete={true} selectionMode="week" detailsAddRender={this.scheduleRender} events={this.state.events} onDisplayRangeChanged={this.displayRangeChanged} onItemAdd={this.handleItemEdit} onBeforeShowAdd={this.beforeShowAddInterface} onBeforeEdit={this.handleBeforeItemEdit} onItemEdit={this.handleItemEdit} detailsItemRender={this.renderDetailsItem} detailsButtonsRender={this.renderDetailsButtons} />
                    {
                        this.state.isLoading ? (
                            <div className="spinner-overlay">
                                {
                                    (typeof this.state.isLoading === "string") ?
                                        <span className="error-text-big">{this.state.isLoading}</span> :
                                        <div className="lds-dual-ring-big" />
                                }
                            </div>
                        ) : ""
                    }
                </div>
            </div>
        );
    }

    private async loadData() {
        try {
            this.setState({
                events: {},
                isLoading: true,
            });

            const beginDate = this.state.displayAfter;
            const endDate = this.state.displayBefore.plus({ millisecond: 1 });

            const parameters = [
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`
            ];

            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }

            const data = await V2.get<any[]>(`schedule/office?${parameters.join("&")}`);

            // Order by location, then date
            data.sort((a, b) => a.location.id.localeCompare(b.location.id) || a.date.localeCompare(b.date));

            const monthBase = DateTime.fromISO(this.state.month);
            const events: { [date: number]: Array<{ title: string, type?: string, length?: number, item?: any, group?: string }>; } = {};

            // Preprocess date to make rendering easier
            for (let i = 0; i < data.length; i++) {
                const item = data[i];

                // Cap date to not before start of display, and start on a sunday
                item.date = DateTime.max(DateTime.fromISO(item.date), beginDate);
                item.date = item.date.minus({ day: item.date.weekday % 7 });

                if (i < data.length - 1 && data[i + 1].location.id === item.location.id) {
                    // Next item is ours, make us fill the gap between weeks
                    let nextDate = DateTime.fromISO(data[i + 1].date);
                    nextDate = nextDate.minus({ day: nextDate.weekday % 7 });

                    item.weeks = nextDate.diff(item.date, "weeks").weeks;
                }
                else {
                    // End of our run, run until end of displayed calendar
                    item.weeks = endDate.diff(item.date as DateTime, "weeks").weeks;
                }
            }

            for (const item of data) {
                let date = item.date;

                let lastOpen = null;
                let lastClose = null;
                let runLength = 0;

                for (let i = 0; i <= 7; i++) {
                    const dayDiff = date.plus({ day: i }).diff(monthBase, "days").days + 1;

                    let open = null;
                    let close = null;
                    switch (i) {
                        case 0: open = item.sundayOpenTime; close = item.sundayCloseTime; break;
                        case 1: open = item.mondayOpenTime; close = item.mondayCloseTime; break;
                        case 2: open = item.tuesdayOpenTime; close = item.tuesdayCloseTime; break;
                        case 3: open = item.wednesdayOpenTime; close = item.wednesdayCloseTime; break;
                        case 4: open = item.thursdayOpenTime; close = item.thursdayCloseTime; break;
                        case 5: open = item.fridayOpenTime; close = item.fridayCloseTime; break;
                        case 6: open = item.saturdayOpenTime; close = item.saturdayCloseTime; break;
                        default: break;
                    }

                    if (i === 7 || (i > 0 && (open !== lastOpen || close !== lastClose))) {
                        if (lastOpen !== null && lastClose !== null) {
                            // Output span
                            for (let r = 0; r < (item.weeks || 1); r++) {
                                const slot = dayDiff - runLength + (r * 7);
                                if (!events[slot])
                                    events[slot] = [];

                                events[slot].push({
                                    title: this.state.searchLocation
                                        ? `${formatTimeLocal(lastOpen)} - ${formatTimeLocal(lastClose)}`
                                        : `${(item.location.shortName || item.location.name)} - (${formatTimeLocal(lastOpen)} - ${formatTimeLocal(lastClose)})`,
                                    type: item.location.color || "",
                                    length: runLength,
                                    item,
                                    group: item.location.id
                                });
                            }

                            if (!item.dayRuns) {
                                item.dayRuns = [];
                            }

                            item.dayRuns.push({
                                start: i - runLength,
                                end: i - 1,
                                openTime: formatTimeLocal(lastOpen),
                                closeTime: formatTimeLocal(lastClose)
                            });
                        }

                        runLength = 0;
                        if (i === 7) {
                            break;
                        }
                    }

                    lastOpen = open;
                    lastClose = close;
                    runLength++;
                }
            }

            this.setState({
                events,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Unable to load schedule"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (name === "month" && !value) {
            return;
        }

        this.setState({
            [name]: value
        }, () => {
            // this.saveSearchState();
            if (name !== "month") {
                this.loadData();
            }
        });
    }

    private displayRangeChanged(minDate: string, maxDate: string) {
        this.setState({
            displayBefore: DateTime.fromISO(maxDate).toLocal(),
            displayAfter: DateTime.fromISO(minDate).toLocal()
        }, () => this.loadData());
    }

    private handleDialogChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        });
    }

    private scheduleRender(state: any) {
        return (
            <div className="input-form">
                <div className="info-block">
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-location" className="required">Location</label>
                            <ValidateInput type="select" id="frm-cal-location" name="calLocation" value={this.state.calLocation} disabled={state.isEdit} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={true}>
                                <option />
                                {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                            </ValidateInput>
                        </div>
                    </section>
                    <label>Weekday hours</label>
                    <section>
                        <div className="input-form-group">
                            <Checkbox fancy={true} id="frm-sunday-enable" name="calSundayEnabled" checked={this.state.calSundayEnabled} onChange={this.handleDialogChange}>Sunday</Checkbox>
                        </div>
                        {
                            this.state.calSundayEnabled
                                ? <div className="input-form-group">
                                    <div className="input-form-group-inline">
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calSundayOpen" value={this.state.calSundayOpen} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calSundayClose" value={this.state.calSundayClose} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                    </div>
                                </div>
                                : <div className="input-form-group">
                                    <em>Closed</em>
                                </div>
                        }
                    </section>
                    <section>
                        <div className="input-form-group">
                            <Checkbox fancy={true} id="frm-monday-enable" name="calMondayEnabled" checked={this.state.calMondayEnabled} onChange={this.handleDialogChange}>Monday</Checkbox>
                        </div>
                        {
                            this.state.calMondayEnabled
                                ? <div className="input-form-group">
                                    <div className="input-form-group-inline">
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calMondayOpen" value={this.state.calMondayOpen} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calMondayClose" value={this.state.calMondayClose} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                    </div>
                                </div>
                                : <div className="input-form-group">
                                    <em>Closed</em>
                                </div>
                        }
                    </section>
                    <section>
                        <div className="input-form-group">
                            <Checkbox fancy={true} id="frm-tuesday-enable" name="calTuesdayEnabled" checked={this.state.calTuesdayEnabled} onChange={this.handleDialogChange}>Tuesday</Checkbox>
                        </div>
                        {
                            this.state.calTuesdayEnabled
                                ? <div className="input-form-group">
                                    <div className="input-form-group-inline">
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calTuesdayOpen" value={this.state.calTuesdayOpen} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calTuesdayClose" value={this.state.calTuesdayClose} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                    </div>
                                </div>
                                : <div className="input-form-group">
                                    <em>Closed</em>
                                </div>
                        }
                    </section>
                    <section>
                        <div className="input-form-group">
                            <Checkbox fancy={true} id="frm-wednesday-enable" name="calWednesdayEnabled" checked={this.state.calWednesdayEnabled} onChange={this.handleDialogChange}>Wednesday</Checkbox>
                        </div>
                        {
                            this.state.calWednesdayEnabled
                                ? <div className="input-form-group">
                                    <div className="input-form-group-inline">
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calWednesdayOpen" value={this.state.calWednesdayOpen} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calWednesdayClose" value={this.state.calWednesdayClose} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                    </div>
                                </div>
                                : <div className="input-form-group">
                                    <em>Closed</em>
                                </div>
                        }
                    </section>
                    <section>
                        <div className="input-form-group">
                            <Checkbox fancy={true} id="frm-thursday-enable" name="calThursdayEnabled" checked={this.state.calThursdayEnabled} onChange={this.handleDialogChange}>Thursday</Checkbox>
                        </div>
                        {
                            this.state.calThursdayEnabled
                                ? <div className="input-form-group">
                                    <div className="input-form-group-inline">
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calThursdayOpen" value={this.state.calThursdayOpen} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calThursdayClose" value={this.state.calThursdayClose} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                    </div>
                                </div>
                                : <div className="input-form-group">
                                    <em>Closed</em>
                                </div>
                        }
                    </section>
                    <section>
                        <div className="input-form-group">
                            <Checkbox fancy={true} id="frm-friday-enable" name="calFridayEnabled" checked={this.state.calFridayEnabled} onChange={this.handleDialogChange}>Friday</Checkbox>
                        </div>
                        {
                            this.state.calFridayEnabled
                                ? <div className="input-form-group">
                                    <div className="input-form-group-inline">
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calFridayOpen" value={this.state.calFridayOpen} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calFridayClose" value={this.state.calFridayClose} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                    </div>
                                </div>
                                : <div className="input-form-group">
                                    <em>Closed</em>
                                </div>
                        }
                    </section>
                    <section>
                        <div className="input-form-group">
                            <Checkbox fancy={true} id="frm-saturday-enable" name="calSaturdayEnabled" checked={this.state.calSaturdayEnabled} onChange={this.handleDialogChange}>Saturday</Checkbox>
                        </div>
                        {
                            this.state.calSaturdayEnabled
                                ? <div className="input-form-group">
                                    <div className="input-form-group-inline">
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calSaturdayOpen" value={this.state.calSaturdayOpen} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                        <div className="input-form-group">
                                            <ValidateInput type="time" name="calSaturdayClose" value={this.state.calSaturdayClose} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false} />
                                        </div>
                                    </div>
                                </div>
                                : <div className="input-form-group">
                                    <em>Closed</em>
                                </div>
                        }
                    </section>
                </div>
            </div>
        );
    }

    private beforeShowAddInterface() {
        return new Promise<void>((resolve, reject) => {
            this.setState({
                calLocation: this.state.searchLocation,
                calOpen: "",
                calClose: "",
            }, () => resolve());
        });
    }

    private async handleItemEdit(date: string, button: string) {
        if (!this.state.calItem) {
            return;
        }

        const itemData = {
            locationId: this.state.calLocation,
            

            sundayOpenTime: this.state.calSundayEnabled ? this.state.calSundayOpen : undefined,
            sundayCloseTime: this.state.calSundayEnabled ? this.state.calSundayClose : undefined,
            mondayOpenTime: this.state.calMondayEnabled ? this.state.calMondayOpen : undefined,
            mondayCloseTime: this.state.calMondayEnabled ? this.state.calMondayClose : undefined,
            tuesdayOpenTime: this.state.calTuesdayEnabled ? this.state.calTuesdayOpen : undefined,
            tuesdayCloseTime: this.state.calTuesdayEnabled ? this.state.calTuesdayClose : undefined,
            wednesdayOpenTime: this.state.calWednesdayEnabled ? this.state.calWednesdayOpen : undefined,
            wednesdayCloseTime: this.state.calWednesdayEnabled ? this.state.calWednesdayClose : undefined,
            thursdayOpenTime: this.state.calThursdayEnabled ? this.state.calThursdayOpen : undefined,
            thursdayCloseTime: this.state.calThursdayEnabled ? this.state.calThursdayClose : undefined,
            fridayOpenTime: this.state.calFridayEnabled ? this.state.calFridayOpen : undefined,
            fridayCloseTime: this.state.calFridayEnabled ? this.state.calFridayClose : undefined,
            saturdayOpenTime: this.state.calSaturdayEnabled ? this.state.calSaturdayOpen : undefined,
            saturdayCloseTime: this.state.calSaturdayEnabled ? this.state.calSaturdayClose : undefined,
        };

        let dt = DateTime.fromISO(date);
        dt = dt.minus({ days: dt.weekday % 7 });

        let mode;
        switch (button) {
            case "save-default": mode = "default"; break;
            default: mode = "single"; break;
        }

        // No try..catch because the calendar dialog handles it
        await V2.put(`schedule/office/${this.state.calLocation}/${dt.toISODate()}?mode=${mode}`, itemData);
        this.loadData();
    }

    private async handleBeforeItemEdit(e: any) {
        if (!e || !e.item) {
            return false;
        }

        this.setState({
            calLocation: e.item.location.id,
            calItem: e.item,

            calSundayEnabled: e.item.sundayOpenTime || e.item.sundayCloseTime,
            calSundayOpen: e.item.sundayOpenTime,
            calSundayClose: e.item.sundayCloseTime,

            calMondayEnabled: e.item.mondayOpenTime || e.item.mondayCloseTime,
            calMondayOpen: e.item.mondayOpenTime,
            calMondayClose: e.item.mondayCloseTime,

            calTuesdayEnabled: e.item.tuesdayOpenTime || e.item.tuesdayCloseTime,
            calTuesdayOpen: e.item.tuesdayOpenTime,
            calTuesdayClose: e.item.tuesdayCloseTime,

            calWednesdayEnabled: e.item.wednesdayOpenTime || e.item.wednesdayCloseTime,
            calWednesdayOpen: e.item.wednesdayOpenTime,
            calWednesdayClose: e.item.wednesdayCloseTime,

            calThursdayEnabled: e.item.thursdayOpenTime || e.item.thursdayCloseTime,
            calThursdayOpen: e.item.thursdayOpenTime,
            calThursdayClose: e.item.thursdayCloseTime,

            calFridayEnabled: e.item.fridayOpenTime || e.item.fridayCloseTime,
            calFridayOpen: e.item.fridayOpenTime,
            calFridayClose: e.item.fridayCloseTime,

            calSaturdayEnabled: e.item.saturdayOpenTime || e.item.saturdayCloseTime,
            calSaturdayOpen: e.item.saturdayOpenTime,
            calSaturdayClose: e.item.saturdayCloseTime,
        });

        return true;
    }

    private renderDetailsItem(item: any) {
        const weekdays = Info.weekdays("short");
        return (
            <span>
                {item.item.location.name}<br />
                {
                    item.item.dayRuns.map((a: any) =>
                        a.start === a.end
                            ? <small key={a.start}>{weekdays[(a.start + 6) % 7]} {a.openTime} - {a.closeTime}<br /></small>
                            : <small key={a.start}>{weekdays[(a.start + 6) % 7]}-{weekdays[(a.end + 6) % 7]} {a.openTime} - {a.closeTime}<br /></small>
                    )
                }
            </span>
        );
    }

    private renderDetailsButtons(callbacks: ICalendar2ButtonCallbacks) {
        return [
            <button key="save-default" className="button btn-light text-with-icon" name="save-default" onClick={callbacks.save}><i className="icon-calendar" /><span>Save as Default</span></button>,
            <button key="save" className="button text-with-icon push-right" onClick={callbacks.save}><i className="icon-check" /><span>Save Week</span></button>
        ];
    }
}

export default withTitle("Office Schedule", withAuth(ScheduleOfficeView));